import { useMemo } from "react";
import { Box, Checkbox, GlobalStyles } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridReadyEvent,
	ICellRendererParams,
} from "ag-grid-community";
import type { ForecastReportYear } from "../models/ForecastReport";

const CENTER_HEADER = "ag-cell-center-header";

const buildColumnDefs = (
	visibleYears: Set<number>,
	onYearToggle: (year: number) => void,
	colorForYear: (year: number) => string,
): ColDef<ForecastReportYear>[] => [
	{
		headerName: "",
		colId: "color",
		width: 40,
		sortable: false,
		filter: false,
		resizable: false,
		cellStyle: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
		},
		cellRenderer: (p: ICellRendererParams<ForecastReportYear>) => {
			if (!p.data) return null;
			return (
				<Box
					sx={{
						width: 12,
						height: 12,
						borderRadius: "50%",
						backgroundColor: colorForYear(p.data.Year),
						border: "1px solid #ACA8A8",
					}}
				/>
			);
		},
	},
	{
		field: "Year",
		headerName: "Year",
		flex: 1,
		sortable: false,
		cellStyle: { textAlign: "center" },
		headerClass: CENTER_HEADER,
	},
	{
		headerName: "",
		colId: "show",
		width: 50,
		sortable: false,
		filter: false,
		resizable: false,
		cellStyle: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
		},
		cellRenderer: (p: ICellRendererParams<ForecastReportYear>) => {
			if (!p.data) return null;
			const checked = visibleYears.has(p.data.Year);
			return (
				<Checkbox
					size="small"
					checked={checked}
					onChange={() => p.data && onYearToggle(p.data.Year)}
					onClick={(e) => e.stopPropagation()}
					sx={{ p: 0 }}
				/>
			);
		},
	},
];

interface Props {
	rows: ForecastReportYear[];
	visibleYears: Set<number>;
	onYearToggle: (year: number) => void;
	colorForYear: (year: number) => string;
	onGridReady?: (event: GridReadyEvent<ForecastReportYear>) => void;
}

export const ForecastReportYearsTable = ({
	rows,
	visibleYears,
	onYearToggle,
	colorForYear,
	onGridReady,
}: Props) => {
	const columnDefs = useMemo(
		() => buildColumnDefs(visibleYears, onYearToggle, colorForYear),
		[visibleYears, onYearToggle, colorForYear],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<AgGridReact<ForecastReportYear>
				columnDefs={columnDefs}
				rowData={rows}
				rowModelType="clientSide"
				defaultColDef={{ resizable: false }}
				suppressContextMenu
				headerHeight={32}
				rowHeight={32}
				onGridReady={onGridReady}
				getRowId={(p) => String(p.data.Year)}
			/>
		</Box>
	);
};
